import type { BlogPosting, Graph, Organization, Person } from 'schema-dts'
import type { StrapiMagazineArticle } from '~/composables/types/strapi/strapi-magazine'
import type { Supplier } from '~/composables/types/event'
import { organizationReference } from '~/composables/schemaOrg/useOrganizationSchema'
import { formatSchemaDate } from '~/utils/schemaOrgHelpers'

export function useMagazineArticleSchema(
  article: StrapiMagazineArticle,
  supplier?: Supplier,
  { url, description }: { url?: string; description?: string } = {}
): any {
  const $i18n = useI18n()
  const $fullLocalizedUrl = useFullLocalizedUrl()

  const schema = ref<Graph | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const partnerUrl = supplierGetters.getPermalink(supplier)
    ? $fullLocalizedUrl(`/p/${supplierGetters.getPermalink(supplier)}/`)
    : undefined

  const getPartnerOrganization = (): Organization | undefined => {
    if (!supplierGetters.getId(supplier)) return undefined

    return {
      '@type': 'Organization',
      name: supplierGetters.getName(supplier),
      ...(partnerUrl && { url: partnerUrl }),
      ...(supplierGetters.getLogo(supplier) && {
        logo: supplierGetters.getLogo(supplier),
      }),
      ...(supplierGetters.getPublicAddress(supplier) && {
        address: supplierGetters.getPublicAddress(supplier),
      }),
    }
  }

  // Mirrors the visible byline: the "about the author" box names a person,
  // so they are the author and the partner is who they work for.
  const getAuthor = (): Person | Organization => {
    const authorInformation = articleGetters.getAuthorInformation(article)
    const partner = getPartnerOrganization()
    const authorUrl =
      articleGetters.getAuthorUrl(article) || (partner && partnerUrl)

    if (authorInformation?.author_name) {
      const image =
        authorInformation.author_picture &&
        strapiImageGetters.getImageUrl(authorInformation.author_picture)

      return {
        '@type': 'Person',
        name: authorInformation.author_name,
        ...(authorInformation.author_description && {
          description: authorInformation.author_description,
        }),
        ...(image && { image }),
        ...(authorUrl && { url: authorUrl }),
        ...(partner && { worksFor: partner }),
      }
    }

    if (partner) return partner

    const name = articleGetters.getAuthor(article).trim()
    if (!name) return organizationReference

    return {
      '@type': 'Person',
      name,
      ...(authorUrl && { url: authorUrl }),
    }
  }

  const createSchema = (): BlogPosting => {
    const firstCategory = articleGetters.getFirstCategory(article)
    const articleSection =
      firstCategory && articleCategoryGetters.getTitle(firstCategory)
    const image =
      articleGetters.getHeaderImageUrl(article) ||
      articleGetters.getSeoImageUrl(article)
    const datePublished = formatSchemaDate(articleGetters.getCreatedAt(article))
    const dateModified = formatSchemaDate(articleGetters.getUpdatedAt(article))

    return {
      '@type': 'BlogPosting',
      headline: articleGetters.getTitle(article) ?? undefined,
      ...(url && {
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      }),
      ...(description && { description }),
      inLanguage: $i18n.locale.value.replace(
        /-(\w+)$/,
        (_: string, region: string) => `-${region.toUpperCase()}`
      ),
      ...(image && { image: [image] }),
      ...(datePublished && { datePublished }),
      ...(dateModified && { dateModified }),
      author: [getAuthor()],
      // only include articleSection when not null
      ...(articleSection ? { articleSection } : {}),
      publisher: organizationReference,
    }
  }

  schema.value = {
    '@context': 'https://schema.org',
    '@graph': [createSchema()],
  }

  return {
    schema,
    script,
  }
}
